import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import type { ExecutorToolRuntimeContext } from "./executorToolRuntime";
import { buildChartToolDescription } from "./chartCapabilityRegistry";
import { getChartImageAsBase64, getCharts } from "./excelHostOps";
import { readRangeContent } from "./excelService";
import type {
  ChartImageFormat,
  ChartingChartInfo,
  ChartingDetail,
  GetChartsOptions,
} from "./services/factset/spreadsheetIntents";
import type { ChartModification, ChartSeriesPayload, WorkbookModification } from "./types";
import { executeModifyWorkbookTool } from "./executorWriteTools";
import {
  beginChartStoppingEvidence,
  finishChartStoppingEvidence,
} from "./executorChartStopping";

export interface RangeChartDependencies {
  getChartImage: typeof getChartImageAsBase64;
  getCharts: typeof getCharts;
  readRange: typeof readRangeContent;
}

const DEFAULT_RANGE_CHART_DEPENDENCIES: RangeChartDependencies = {
  getChartImage: getChartImageAsBase64,
  getCharts,
  readRange: readRangeContent,
};

export type RangeChartOperation =
  | "read_range_chart"
  | "create_range_chart"
  | "update_range_chart"
  | "add_range_chart_series"
  | "update_range_chart_series"
  | "remove_range_chart_series"
  | "get_range_chart_image";

interface RangeChartInput {
  operation: RangeChartOperation | null;
  stepId: string | null;
  workbookName: string | null;
  sheetName: string | null;
  sheetRef?: GetChartsOptions["sheetRef"];
  chartIndex: number | null;
  detail: ChartingDetail | null;
  sourceRange: string | null;
  chartType: string | null;
  title: string | null;
  position?: ChartModification["position"];
  legend?: Record<string, unknown>;
  axes?: unknown[];
  chartArea?: Record<string, unknown>;
  plotArea?: Record<string, unknown>;
  chartInfo?: Record<string, unknown>;
  seriesIndex: number | null;
  series?: ChartSeriesPayload;
  imageFormat: ChartImageFormat | "Jpeg" | null;
  includeDataUri: boolean;
}

const nullableString = { anyOf: [{ type: "string" }, { type: "null" }] };
const nullableNumber = { anyOf: [{ type: "number" }, { type: "null" }] };

const chartPositionSchema = {
  type: "object",
  properties: {
    top: { type: "number" },
    left: { type: "number" },
    width: { type: "number" },
    height: { type: "number" },
    startCell: {
      type: "string",
      description: "Optional cell anchor such as E2. Prefer point coordinates when known.",
    },
  },
  additionalProperties: false,
};

const sheetRefSchema = {
  type: "object",
  properties: {
    index: { type: "number" },
    name: { type: "string" },
    codeName: { type: "string" },
  },
  additionalProperties: false,
};

const seriesSchema = {
  type: "object",
  additionalProperties: true,
  description:
    "Series payload for add_range_chart_series/update_range_chart_series. Use Excel-style ranges like Sheet1!B2:B10 for values/categories. " +
    "For colors, prefer fill.colorRgb / line.colorRgb with #RRGGBB.",
  properties: {
    values: { type: "string" },
    categories: { type: "string" },
    name: { type: "string" },
    chartType: { type: "string" },
    axisGroup: { type: "string" },
    fillColor: { type: "string" },
    lineColor: { type: "string" },
    lineWeight: { type: "number" },
    fill: { type: "object", additionalProperties: true },
    line: { type: "object", additionalProperties: true },
    marker: { type: "object", additionalProperties: true },
    dataLabels: { type: "object", additionalProperties: true },
    points: { type: "array", items: { type: "object", additionalProperties: true } },
  },
};

export const RANGE_CHART_TOOL: ChartCapabilityToolDef = {
  name: "range_chart",
  description: buildChartToolDescription("range_chart"),
  inputSchema: {
    type: "object",
    additionalProperties: false,
    properties: {
      operation: {
        type: "string",
        enum: [
          "read_range_chart",
          "create_range_chart",
          "update_range_chart",
          "add_range_chart_series",
          "update_range_chart_series",
          "remove_range_chart_series",
          "get_range_chart_image",
        ],
      },
      stepId: {
        ...nullableString,
        description:
          "Required for write operations after the internal run step. Use null only for read_range_chart/get_range_chart_image.",
      },
      workbookName: {
        ...nullableString,
        description:
          "Optional workbook target. Runtime defaults to the pinned workbook when available.",
      },
      sheetName: {
        ...nullableString,
        description:
          "Worksheet name containing the chart. Required for write operations so chart targeting is deterministic.",
      },
      sheetRef: sheetRefSchema,
      chartIndex: {
        ...nullableNumber,
        description:
          "0-based chart index on the target sheet. Required for edit/series write operations.",
      },
      detail: { type: "string", enum: ["Summary", "Full"] },
      sourceRange: {
        ...nullableString,
        description: "A1 source range for create, e.g. A1:C10.",
      },
      chartType: {
        ...nullableString,
        description: "Excel xl* chart type, e.g. xlColumnClustered or xlLine.",
      },
      title: nullableString,
      position: chartPositionSchema,
      chartInfo: {
        type: "object",
        additionalProperties: true,
        description:
          "Chart-level properties for create/update: title, legend, axes, chartArea, plotArea, plotBy, etc. Series changes must use series operations.",
      },
      legend: {
        type: "object",
        additionalProperties: true,
        description: "Convenience chartInfo.legend override.",
      },
      axes: {
        type: "array",
        items: { type: "object", additionalProperties: true },
        description: "Convenience chartInfo.axes override.",
      },
      chartArea: {
        type: "object",
        additionalProperties: true,
        description: "Convenience chartInfo.chartArea override.",
      },
      plotArea: {
        type: "object",
        additionalProperties: true,
        description: "Convenience chartInfo.plotArea override.",
      },
      seriesIndex: {
        ...nullableNumber,
        description:
          "0-based series index for update_range_chart_series/remove_range_chart_series.",
      },
      series: seriesSchema,
      imageFormat: {
        ...nullableString,
        description:
          "Optional image format for get_range_chart_image. Host currently returns PNG regardless; accepted values include Png, Jpg, Bmp, Gif.",
      },
      includeDataUri: {
        type: "boolean",
        description:
          "For get_range_chart_image, include the full data URI in the tool result. Default false to keep context compact.",
      },
    },
    required: ["operation"],
  },
};

export const EXECUTOR_RANGE_CHART_TOOLS: readonly ChartCapabilityToolDef[] = [
  RANGE_CHART_TOOL,
];

export async function executeRangeChartTool(
  ctx: ExecutorToolRuntimeContext,
  rawInput: unknown,
  dependencies: RangeChartDependencies = DEFAULT_RANGE_CHART_DEPENDENCIES,
): Promise<string> {
  const input = normalizeRangeChartInput(rawInput);
  switch (input.operation) {
    case "read_range_chart":
      return executeReadRangeCharts(ctx, input, dependencies);
    case "get_range_chart_image":
      return executeGetRangeChartImage(ctx, input, dependencies);
    case "create_range_chart":
    case "update_range_chart":
    case "add_range_chart_series":
    case "update_range_chart_series":
    case "remove_range_chart_series":
      return executeRangeChartWrite(ctx, input, dependencies);
    default:
      return JSON.stringify({
        ok: false,
        error: "range_chart requires operation.",
      });
  }
}

async function executeReadRangeCharts(
  ctx: ExecutorToolRuntimeContext,
  input: RangeChartInput,
  dependencies: RangeChartDependencies,
): Promise<string> {
  try {
    const charts = await dependencies.getCharts({
      workbookName: input.workbookName ?? ctx.session.pinnedWorkbookName,
      sheetRef: sheetRefFromInput(input),
      type: "Chart",
      ...(input.chartIndex !== null ? { chartIndex: input.chartIndex } : {}),
      detail: input.detail ?? "Full",
    });
    return JSON.stringify({
      ok: true,
      total: charts.length,
      charts: charts.map(trimNativeChartInfo),
    });
  } catch (error) {
    return JSON.stringify({
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

async function executeGetRangeChartImage(
  ctx: ExecutorToolRuntimeContext,
  input: RangeChartInput,
  dependencies: RangeChartDependencies,
): Promise<string> {
  try {
    const dataUri = await dependencies.getChartImage({
      workbookName: input.workbookName ?? ctx.session.pinnedWorkbookName,
      sheetRef: sheetRefFromInput(input),
      ...(input.chartIndex !== null ? { chartIndex: input.chartIndex } : {}),
      ...(input.imageFormat ? { format: input.imageFormat } : {}),
    });
    return JSON.stringify({
      ok: true,
      chartIndex: input.chartIndex,
      dataUriLength: dataUri.length,
      dataUriPreview: dataUri.slice(0, 80),
      ...(input.includeDataUri ? { dataUri } : {}),
    });
  } catch (error) {
    return JSON.stringify({
      ok: false,
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

async function executeRangeChartWrite(
  ctx: ExecutorToolRuntimeContext,
  input: RangeChartInput,
  dependencies: RangeChartDependencies,
): Promise<string> {
  const stepId = beginChartStoppingEvidence(ctx, {
    stepId: input.stepId,
    tool: "range_chart",
    operation: input.operation ?? "range_chart_write",
  });
  const stopFailure = (error: string, priorOutput?: Record<string, unknown>) => {
    if (stepId) {
      finishChartStoppingEvidence(ctx, {
        stepId,
        tool: "range_chart",
        operation: input.operation ?? "range_chart_write",
        satisfied: false,
        reason: error,
      });
    }
    return JSON.stringify({
      ...(priorOutput ?? {}),
      ok: false,
      applied: false,
      stepId: input.stepId,
      error,
      completionEvidence: { satisfied: false },
    });
  };
  const error = validateWriteTarget(ctx, input);
  if (error) return stopFailure(error);

  const chartMod = chartModificationFromInput(input);
  if (typeof chartMod === "string") return stopFailure(chartMod);

  const workbookName = input.workbookName ?? ctx.session.pinnedWorkbookName;
  let sourceObserved = input.operation !== "create_range_chart";
  if (input.operation === "create_range_chart") {
    const pendingRefresh =
      ctx.accumulators.deferredRefreshAllRequested ||
      Array.from(ctx.accumulators.fdsRefreshSheets).some(
        (sheetName) =>
          sheetName.trim().toLowerCase() ===
          input.sheetName!.trim().toLowerCase(),
      );
    const pendingRecalc = ctx.accumulators.deferredRecalcMode !== null;
    if (pendingRefresh || pendingRecalc) {
      return stopFailure(
        "Range-chart creation is blocked until pending FactSet refresh and workbook recalculation debt is cleared.",
      );
    }
    try {
      const source = await dependencies.readRange(
        input.sheetName!,
        input.sourceRange!,
        { includeFormulas: true, includeFormats: false },
        workbookName,
      );
      sourceObserved =
        Boolean(source.range) &&
        source.truncated !== true &&
        source.emptyRange !== true &&
        (source.range?.values.some((row) =>
          row.some((value) => value !== null && value !== "")
        ) ?? false);
      if (!sourceObserved) {
        return stopFailure(
          `Range-chart source ${input.sheetName}!${input.sourceRange} was not observed as a non-empty, untruncated range.`,
        );
      }
    } catch (readError) {
      return stopFailure(
        `Could not observe range-chart source: ${
          readError instanceof Error ? readError.message : String(readError)
        }`,
      );
    }
  }

  let chartsBefore: ChartingChartInfo[] = [];
  try {
    chartsBefore = await dependencies.getCharts({
      workbookName,
      sheetRef: { name: input.sheetName! },
      type: "Chart",
      detail: "Summary",
    });
  } catch (readError) {
    return stopFailure(
      `Could not establish the pre-write chart state: ${
        readError instanceof Error ? readError.message : String(readError)
      }`,
    );
  }

  const writeOutput = await executeModifyWorkbookTool(ctx, {
    stepId: input.stepId,
    targetSheet: input.sheetName,
    modifications: {
      worksheets: [
        {
          sheetName: input.sheetName,
          charts: [chartMod],
        },
      ],
      message: nativeChartWriteMessage(input, chartMod),
    } satisfies WorkbookModification,
  });
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(writeOutput) as Record<string, unknown>;
  } catch {
    return stopFailure("Range-chart write returned an unreadable acknowledgement.");
  }
  if (
    parsed.applied !== true ||
    parsed.error ||
    (Array.isArray(parsed.failedActions) && parsed.failedActions.length > 0) ||
    (Array.isArray(parsed.skippedActions) && parsed.skippedActions.length > 0)
  ) {
    return stopFailure(
      typeof parsed.error === "string"
        ? parsed.error
        : "Range-chart write was not fully acknowledged by Excel.",
      parsed,
    );
  }
  // Excel has acknowledged the chart, so the read below can only describe what
  // landed — it can no longer turn this into a failure. A model told the write
  // failed draws the chart a second time, and chart metadata that lags or times
  // out is not evidence that nothing was drawn. Unconfirmed is reported as
  // unconfirmed, the same way the native_chart path treats a read after a
  // mutation it already completed.
  let chartsAfter: ChartingChartInfo[] = [];
  let verificationWarning: string | undefined;
  try {
    chartsAfter = await dependencies.getCharts({
      workbookName,
      sheetRef: { name: input.sheetName! },
      type: "Chart",
      ...(input.operation !== "create_range_chart" && input.chartIndex !== null
        ? { chartIndex: input.chartIndex }
        : {}),
      detail: "Full",
    });
    const chartVerified =
      input.operation === "create_range_chart"
        ? chartsAfter.length > chartsBefore.length
        : chartsAfter.length === 1;
    if (!chartVerified) {
      verificationWarning =
        input.operation === "create_range_chart"
          ? `Excel applied the chart write but did not report a new chart on ${input.sheetName}, so the chart itself is unconfirmed. Do not create it again; tell the user it was written and ask them to check the sheet.`
          : `Excel applied the chart write but did not report the intended chart on ${input.sheetName}, so the change itself is unconfirmed. Do not repeat the write; tell the user it was applied and ask them to check the sheet.`;
    }
  } catch (readError) {
    verificationWarning = `Excel applied the chart write but did not return this sheet's chart metadata (${
      readError instanceof Error ? readError.message : String(readError)
    }), so the chart itself is unconfirmed. Do not repeat the write; tell the user it was applied and ask them to check the sheet.`;
  }
  const nativeChartVerified = verificationWarning === undefined;
  if (stepId) {
    finishChartStoppingEvidence(ctx, {
      stepId,
      tool: "range_chart",
      operation: input.operation ?? "range_chart_write",
      satisfied: true,
      details: {
        sourceObserved,
        refreshDebtCleared: true,
        writeSucceeded: true,
        nativeChartVerified,
      },
      ...(verificationWarning ? { warning: verificationWarning } : {}),
    });
  }
  return JSON.stringify({
    ...parsed,
    ok: true,
    applied: true,
    charts: chartsAfter.map(trimNativeChartInfo),
    ...(verificationWarning ? { warning: verificationWarning } : {}),
    completionEvidence: {
      satisfied: true,
      sourceObserved,
      refreshDebtCleared: true,
      writeSucceeded: true,
      nativeChartVerified,
    },
  });
}

function chartModificationFromInput(input: RangeChartInput): ChartModification | string {
  const base: Partial<ChartModification> = {
    ...(input.chartIndex !== null ? { chartIndex: input.chartIndex } : {}),
    ...(input.workbookName ? { workbookName: input.workbookName } : {}),
  };
  if (input.operation === "create_range_chart") {
    if (!input.sourceRange) return "create_range_chart requires sourceRange.";
    if (!input.chartType) return "create_range_chart requires chartType.";
    const chartInfo = buildRangeChartInfo(input, { inferPlotBy: true });
    return {
      ...base,
      action: "create",
      sourceRange: input.sourceRange,
      chartType: input.chartType,
      ...(input.title ? { title: input.title } : {}),
      ...(input.position ? { position: input.position } : {}),
      ...(chartInfo ? { chartInfo } : {}),
    } as ChartModification;
  }
  if (input.operation === "update_range_chart") {
    const chartInfo = buildRangeChartInfo(input, { includeChartType: true });
    if (Object.keys(chartInfo).length === 0) {
      return "update_range_chart requires chartInfo, chartType, title, legend, axes, chartArea, or plotArea.";
    }
    return {
      ...base,
      action: "update",
      chartInfo,
      ...(input.chartType ? { chartType: input.chartType } : {}),
      ...(input.title !== null ? { title: input.title } : {}),
    } as ChartModification;
  }
  if (input.operation === "add_range_chart_series") {
    if (!input.series?.values) return "add_range_chart_series requires series.values.";
    return {
      ...base,
      action: "addSeries",
      series: input.series,
    } as ChartModification;
  }
  if (input.operation === "update_range_chart_series") {
    if (input.seriesIndex === null) return "update_range_chart_series requires seriesIndex.";
    if (!input.series || Object.keys(input.series).length === 0) {
      return "update_range_chart_series requires a non-empty series body.";
    }
    return {
      ...base,
      action: "updateSeries",
      seriesIndex: input.seriesIndex,
      series: input.series,
    } as ChartModification;
  }
  if (input.operation === "remove_range_chart_series") {
    if (input.seriesIndex === null) return "remove_range_chart_series requires seriesIndex.";
    return {
      ...base,
      action: "removeSeries",
      seriesIndex: input.seriesIndex,
    } as ChartModification;
  }
  return `Unsupported write operation: ${input.operation ?? "missing"}`;
}

function nativeChartWriteMessage(
  input: RangeChartInput,
  chartMod: ChartModification,
): string {
  const sheet = input.sheetName ?? "active sheet";
  switch (chartMod.action) {
    case "create":
      return `Created range chart from ${sheet}!${chartMod.sourceRange}`;
    case "update":
      return `Updated range chart #${chartMod.chartIndex ?? 0} on ${sheet}`;
    case "addSeries":
      return `Added series to range chart #${chartMod.chartIndex ?? 0} on ${sheet}`;
    case "updateSeries":
      return `Updated range chart series #${chartMod.seriesIndex} on ${sheet}`;
    case "removeSeries":
      return `Removed range chart series #${chartMod.seriesIndex} on ${sheet}`;
    default:
      return `Modified range chart on ${sheet}`;
  }
}

function validateWriteTarget(
  ctx: ExecutorToolRuntimeContext,
  input: RangeChartInput,
): string | null {
  if (ctx.planState.steps.length === 0) {
    return "Workbook edits require the internal native run step.";
  }
  if (!input.stepId) return "Write operations require the internal native stepId.";
  if (!ctx.planState.steps.some((step) => step.id === input.stepId)) {
    return `No run step found for stepId "${input.stepId}". Use the internal native stepId.`;
  }
  if (!input.sheetName) return "Write operations require sheetName.";
  if (input.operation !== "create_range_chart" && input.chartIndex === null) {
    return `${input.operation ?? "write operation"} requires chartIndex. Call read_range_chart first if the target chart index is unknown.`;
  }
  return null;
}

function normalizeRangeChartInput(raw: unknown): RangeChartInput {
  const record = raw && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};
  const operation = normalizeRangeChartOperation(record.operation);
  return {
    operation,
    stepId: normalizeNullableString(record.stepId),
    workbookName: normalizeNullableString(record.workbookName),
    sheetName: normalizeNullableString(record.sheetName),
    sheetRef: normalizeSheetRef(record.sheetRef),
    chartIndex: normalizeNullableNumber(record.chartIndex),
    detail: record.detail === "Summary" || record.detail === "Full" ? record.detail : null,
    sourceRange: normalizeNullableString(record.sourceRange),
    chartType: normalizeNullableString(record.chartType),
    title: normalizeNullableString(record.title),
    position: normalizePosition(record.position),
    legend: normalizeRecord(record.legend),
    axes: Array.isArray(record.axes) ? record.axes as unknown[] : undefined,
    chartArea: normalizeRecord(record.chartArea),
    plotArea: normalizeRecord(record.plotArea),
    chartInfo: normalizeRecord(record.chartInfo),
    seriesIndex: normalizeNullableNumber(record.seriesIndex),
    series: normalizeSeries(record.series),
    imageFormat: normalizeImageFormat(record.imageFormat),
    includeDataUri: record.includeDataUri === true,
  };
}

/**
 * Accept old persisted operation ids at dispatch time while exposing only the
 * range_chart ids in the model-facing schema.
 */
export function normalizeRangeChartOperation(
  value: unknown,
): RangeChartOperation | null {
  const operation = typeof value === "string" ? value : "";
  const aliases: Readonly<Record<string, RangeChartOperation>> = {
    read_range_chart: "read_range_chart",
    create_range_chart: "create_range_chart",
    update_range_chart: "update_range_chart",
    add_range_chart_series: "add_range_chart_series",
    update_range_chart_series: "update_range_chart_series",
    remove_range_chart_series: "remove_range_chart_series",
    get_range_chart_image: "get_range_chart_image",
    read_native_graph: "read_range_chart",
    create_native_graph: "create_range_chart",
    edit_native_graph: "update_range_chart",
    update_native_graph: "update_range_chart",
    add_native_graph_series: "add_range_chart_series",
    update_native_graph_series: "update_range_chart_series",
    remove_native_graph_series: "remove_range_chart_series",
    get_native_graph_image: "get_range_chart_image",
  };
  return aliases[operation] ?? null;
}

function normalizeNullableString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function normalizeNullableNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function normalizeRecord(value: unknown): Record<string, unknown> | undefined {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function normalizePosition(value: unknown): ChartModification["position"] | undefined {
  return normalizeRecord(value) as ChartModification["position"] | undefined;
}

function normalizeSeries(value: unknown): ChartSeriesPayload | undefined {
  return normalizeRecord(value) as unknown as ChartSeriesPayload | undefined;
}

function normalizeSheetRef(value: unknown): GetChartsOptions["sheetRef"] | undefined {
  const record = normalizeRecord(value);
  if (!record) return undefined;
  const index = normalizeNullableNumber(record.index);
  const name = normalizeNullableString(record.name);
  const codeName = normalizeNullableString(record.codeName);
  const sheetRef: NonNullable<GetChartsOptions["sheetRef"]> = {};
  if (index !== null) sheetRef.index = index;
  if (name !== null) sheetRef.name = name;
  if (codeName !== null) sheetRef.codeName = codeName;
  return Object.keys(sheetRef).length > 0 ? sheetRef : undefined;
}

function normalizeImageFormat(value: unknown): ChartImageFormat | "Jpeg" | null {
  return value === "Png" || value === "Jpg" || value === "Jpeg" || value === "Bmp" || value === "Gif"
    ? value
    : null;
}

function sheetRefFromInput(
  input: RangeChartInput,
): GetChartsOptions["sheetRef"] | undefined {
  return input.sheetName ? { name: input.sheetName } : input.sheetRef;
}

function buildRangeChartInfo(
  input: RangeChartInput,
  options: { inferPlotBy?: boolean; includeChartType?: boolean } = {},
): Record<string, unknown> {
  const inferredPlotBy =
    options.inferPlotBy && input.sourceRange
      ? inferDefaultPlotBy(input.sourceRange)
      : undefined;
  const chartInfo: Record<string, unknown> = {
    ...(inferredPlotBy ? { plotBy: inferredPlotBy } : {}),
    ...(input.chartInfo ?? {}),
    ...(options.includeChartType && input.chartType ? { chartType: input.chartType } : {}),
    ...(input.title !== null ? { title: { hasTitle: true, text: input.title } } : {}),
    ...(input.legend ? { legend: input.legend } : {}),
    ...(input.axes ? { axes: input.axes } : {}),
    ...(input.chartArea ? { chartArea: input.chartArea } : {}),
    ...(input.plotArea ? { plotArea: input.plotArea } : {}),
  };
  return chartInfo;
}

function inferDefaultPlotBy(sourceRange: string): "xlColumns" | "xlRows" | undefined {
  const dimensions = parseA1RangeDimensions(sourceRange);
  if (!dimensions) return undefined;
  return dimensions.columnCount > dimensions.rowCount ? "xlRows" : "xlColumns";
}

function parseA1RangeDimensions(
  sourceRange: string,
): { rowCount: number; columnCount: number } | undefined {
  const bang = sourceRange.lastIndexOf("!");
  const bare = (bang >= 0 ? sourceRange.slice(bang + 1) : sourceRange)
    .replace(/^'|'$/g, "")
    .replace(/\$/g, "");
  const [startRef, endRef = startRef] = bare.split(":");
  const start = parseA1CellRef(startRef);
  const end = parseA1CellRef(endRef);
  if (!start || !end) return undefined;
  return {
    rowCount: Math.abs(end.row - start.row) + 1,
    columnCount: Math.abs(end.column - start.column) + 1,
  };
}

function parseA1CellRef(cellRef: string): { row: number; column: number } | undefined {
  const match = /^([A-Z]+)(\d+)$/i.exec(cellRef.trim());
  if (!match) return undefined;
  return {
    column: columnLettersToNumber(match[1]),
    row: parseInt(match[2], 10),
  };
}

function columnLettersToNumber(letters: string): number {
  return letters
    .toUpperCase()
    .split("")
    .reduce((acc, char) => acc * 26 + (char.charCodeAt(0) - 64), 0);
}

function trimNativeChartInfo(chart: ChartingChartInfo): Record<string, unknown> {
  return {
    type: chart.type,
    id: chart.id,
    index: chart.index,
    sheetRef: chart.sheetRef,
    name: chart.name,
    chartType: chart.chartType,
    title: chart.title?.text,
    position: chart.position,
    plotBy: chart.plotBy,
    legend: chart.legend,
    axes: chart.axes,
    series: chart.series?.map((series) => ({
      index: series.index,
      name: series.name,
      values: series.values,
      categories: series.categories,
      chartType: series.chartType,
      axisGroup: series.axisGroup,
      fill: series.fill,
      line: series.line,
      marker: series.marker,
      dataLabels: series.dataLabels,
      points: series.points,
    })),
  };
}
